import { useParams, Link, Navigate } from "react-router-dom";
import {
  ShieldAlert, FlaskConical, Users, Car, FileWarning, Gavel, Banknote,
  Building2, Lock, Globe2, ShieldCheck, HardHat, Landmark, BadgeCheck, Scale, ArrowRight,
} from "lucide-react";
import { getGroup, SERVICE_GROUPS, LEGACY_SERVICE_MAP } from "@/data/servicesIA";
import { PageHero, Breadcrumbs, FadeIn } from "@/components/Shared";
import ServiceDetailBody from "@/components/ServiceDetailBody";

const ICONS = {
  ShieldAlert, FlaskConical, Users, Car, FileWarning, Gavel, Banknote,
  Building2, Lock, Globe2, ShieldCheck, HardHat, Landmark, BadgeCheck, Scale,
};

export default function ServiceGroup() {
  const { group: groupSlug } = useParams();
  const group = getGroup(groupSlug);

  // Legacy flat-slug redirects (old /services/fraud etc.)
  if (!group) {
    const legacy = LEGACY_SERVICE_MAP[groupSlug];
    return <Navigate to={legacy || "/services"} replace />;
  }

  const related = SERVICE_GROUPS.filter((g) => g.slug !== groupSlug)
    .slice(0, 4)
    .map((g) => ({ label: g.title, href: `/services/${g.slug}` }));

  return (
    <main data-testid="service-group-page">
      <PageHero eyebrow="Our Services" title={group.title} lead={group.tagline} />
      <Breadcrumbs items={[{ label: "Our Services", href: "/services" }, { label: group.title }]} />

      {group.children.length > 0 ? (
        <>
          <section className="bg-white pb-4 pt-2">
            <div className="brand-container">
              <FadeIn>
                <p className="max-w-3xl text-lg font-light leading-relaxed text-slate-600">
                  {group.summary}
                </p>
              </FadeIn>
            </div>
          </section>
          <section className="brand-panel-pale py-20 sm:py-28 lg:py-32">
            <div className="brand-container">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {group.children.map((child, i) => {
                  const Icon = ICONS[child.icon] || Scale;
                  return (
                    <FadeIn key={child.slug} delay={(i % 3) * 0.08} className="h-full">
                      <Link
                        to={`/services/${group.slug}/${child.slug}`}
                        className="brand-card group flex h-full flex-col p-8"
                        data-testid={`offence-card-${child.slug}`}
                      >
                        <Icon className="size-7 text-brand-gold" />
                        <h3 className="mt-8 text-lg font-medium text-brand-navy">{child.title}</h3>
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">
                          {child.tagline}
                        </p>
                        <span className="brand-text-link mt-6 flex items-center gap-2">
                          Read more
                          <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                        </span>
                      </Link>
                    </FadeIn>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      ) : (
        <ServiceDetailBody item={group} related={related} />
      )}
    </main>
  );
}
